import React from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { ENGINES, GOLD, resolve } from "./showKit";

const ease = [0.22, 1, 0.36, 1];
export const rise = (i = 0, d = 0.12) => ({ initial: { opacity: 0, y: 40, filter: "blur(8px)" }, animate: { opacity: 1, y: 0, filter: "blur(0px)" }, transition: { duration: 1.1, delay: 0.3 + i * d, ease } });

export function Title({ p }) {
  return (
    <div className="max-w-5xl">
      <motion.div {...rise(0)} className="font-mono text-sm md:text-base tracking-[0.5em] uppercase" style={{ color: GOLD }}>{p.kicker}</motion.div>
      <motion.h1 {...rise(1, 0.25)} className="font-serif text-5xl md:text-7xl lg:text-8xl font-bold leading-[1.02] mt-6 text-white">{p.title}</motion.h1>
      <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 1.6, delay: 0.9, ease }} className="h-[2px] w-64 origin-left mt-8" style={{ background: `linear-gradient(90deg, ${GOLD}, transparent)` }} />
      <motion.p {...rise(3, 0.25)} className="text-xl md:text-2xl text-white/70 mt-6 font-light">{p.sub}</motion.p>
    </div>
  );
}

export function Statement({ p }) {
  const words = p.text.split(" ");
  return (
    <div className="max-w-5xl">
      <h2 className="font-serif italic text-4xl md:text-6xl lg:text-7xl leading-tight text-white">
        {words.map((w, i) => <motion.span key={i} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.4 + i * 0.09, ease }} className="inline-block mr-[0.25em]">{w}</motion.span>)}
      </h2>
      <motion.div {...rise(words.length * 0.6)} className="font-mono text-sm tracking-[0.45em] uppercase mt-10" style={{ color: GOLD }}>{p.sub}</motion.div>
    </div>
  );
}

export function Engines() {
  const R = 210;
  return (
    <div className="relative w-[560px] h-[560px] max-w-[90vw] max-h-[60vh] aspect-square">
      <motion.div initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 1.2, ease }} className="absolute inset-0 m-auto w-40 h-40 rounded-full grid place-items-center text-center"
        style={{ background: `radial-gradient(circle, ${GOLD}55, transparent 70%)` }}>
        <span className="font-serif text-2xl text-white leading-tight">One<br />record</span>
      </motion.div>
      <motion.div animate={{ rotate: 360 }} transition={{ duration: 120, repeat: Infinity, ease: "linear" }} className="absolute inset-0">
        <svg viewBox="-280 -280 560 560" className="absolute inset-0 w-full h-full">
          <motion.circle r={R} fill="none" stroke="rgba(255,255,255,0.12)" strokeDasharray="4 8" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 2 }} />
          {ENGINES.map(([, c], i) => { const a = (i / 8) * Math.PI * 2; return <motion.line key={i} x1="0" y1="0" x2={Math.cos(a) * R} y2={Math.sin(a) * R} stroke={c} strokeOpacity="0.35" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1, delay: 0.8 + i * 0.25 }} />; })}
        </svg>
        {ENGINES.map(([name, c], i) => {
          const a = (i / 8) * Math.PI * 2;
          return (
            <div key={name} className="absolute" style={{ left: `${50 + 37.5 * Math.cos(a)}%`, top: `${50 + 37.5 * Math.sin(a)}%`, transform: "translate(-50%, -50%)" }}>
              <motion.div initial={{ opacity: 0, scale: 0.3 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, delay: 0.8 + i * 0.25, ease }}>
                <motion.div animate={{ rotate: -360 }} transition={{ duration: 120, repeat: Infinity, ease: "linear" }} className="flex flex-col items-center gap-2">
                  <span className="w-14 h-14 rounded-2xl grid place-items-center border" style={{ borderColor: c, background: `${c}22`, boxShadow: `0 0 40px ${c}55` }}><span className="w-3 h-3 rounded-full" style={{ background: c }} /></span>
                  <span className="text-sm md:text-base text-white font-semibold whitespace-nowrap">{name}</span>
                </motion.div>
              </motion.div>
            </div>
          );
        })}
      </motion.div>
    </div>
  );
}

export function Flow({ p }) {
  return (
    <div className="w-full max-w-6xl flex flex-wrap md:flex-nowrap items-center justify-center gap-y-6">
      {p.steps.map((s, i) => (
        <React.Fragment key={s}>
          <motion.div {...rise(i, 0.45)} className="relative rounded-2xl border border-white/15 bg-white/[0.04] backdrop-blur-xl px-5 py-6 text-center min-w-[140px]">
            <div className="font-mono text-xs tracking-[0.3em]" style={{ color: GOLD }}>0{i + 1}</div>
            <div className="text-lg md:text-xl text-white font-semibold mt-2">{s}</div>
            <motion.span className="absolute inset-0 rounded-2xl" initial={{ boxShadow: "0 0 0 rgba(212,160,23,0)" }} animate={{ boxShadow: ["0 0 0px rgba(212,160,23,0)", "0 0 40px rgba(212,160,23,0.45)", "0 0 0px rgba(212,160,23,0)"] }} transition={{ duration: 1.6, delay: 0.8 + i * 0.45 }} />
          </motion.div>
          {i < p.steps.length - 1 && <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 0.5, delay: 0.6 + i * 0.45 }} className="hidden md:block h-[2px] w-10 origin-left" style={{ background: GOLD }} />}
        </React.Fragment>
      ))}
    </div>
  );
}

function Counter({ value }) {
  const m = String(value).match(/^([^0-9]*)([0-9,.]+)(.*)$/);
  const [n, setN] = React.useState(0);
  const target = m ? Number(m[2].replace(/,/g, "")) : 0;
  React.useEffect(() => {
    if (!m) return undefined;
    let raf; const t0 = performance.now();
    const tick = (t) => { const k = Math.min(1, (t - t0) / 2200); setN(target * (1 - Math.pow(1 - k, 3))); if (k < 1) raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!m) return <>{value}</>;
  const dec = m[2].includes(".") ? 2 : 0;
  return <>{m[1]}{n.toLocaleString(undefined, { minimumFractionDigits: dec, maximumFractionDigits: dec })}{m[3]}</>;
}

export function Stats({ p, data }) {
  return (
    <div className={`grid gap-6 w-full max-w-6xl ${p.items.length > 3 ? "grid-cols-2 lg:grid-cols-4" : p.items.length === 1 ? "grid-cols-1 max-w-md" : "grid-cols-1 md:grid-cols-3"}`}>
      {p.items.map(([v, label], i) => (
        <motion.div key={label} {...rise(i, 0.35)} className="rounded-3xl border border-white/10 bg-white/[0.035] backdrop-blur-xl p-8">
          <div className="font-serif text-5xl md:text-6xl font-bold text-white tabular-nums" data-testid={`show-stat-${i}`}><Counter value={resolve(v, data)} /></div>
          <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 1.2, delay: 0.8 + i * 0.35 }} className="h-[2px] w-16 origin-left my-4" style={{ background: GOLD }} />
          <div className="text-base md:text-lg text-white/70">{label}</div>
        </motion.div>
      ))}
    </div>
  );
}

export function Checks({ p }) {
  return (
    <div className="grid md:grid-cols-2 gap-4 w-full max-w-5xl">
      {p.items.map((t, i) => (
        <motion.div key={t} {...rise(i, 0.4)} className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl px-6 py-5">
          <motion.span initial={{ scale: 0, rotate: -90 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 260, damping: 16, delay: 0.9 + i * 0.4 }}
            className="w-10 h-10 shrink-0 rounded-full grid place-items-center bg-emerald-400 text-black"><Check className="w-6 h-6" strokeWidth={3} /></motion.span>
          <span className="text-lg md:text-2xl text-white">{t}</span>
        </motion.div>
      ))}
    </div>
  );
}

export function Docs({ p }) {
  return (
    <div className="flex flex-wrap justify-center gap-6 max-w-6xl">
      {p.items.map((t, i) => (
        <motion.div key={t} initial={{ opacity: 0, y: 60, rotate: -6 + i * 3 }} animate={{ opacity: 1, y: 0, rotate: -3 + i * 1.5 }} transition={{ duration: 1, delay: 0.3 + i * 0.35, ease }}
          className="relative w-44 h-56 rounded-xl bg-white/[0.92] p-4 shadow-2xl">
          <div className="text-xs font-bold text-slate-800 uppercase tracking-wide">{t}</div>
          {[0, 1, 2, 3, 4, 5].map((l) => <div key={l} className="h-1.5 rounded bg-slate-300 mt-3" style={{ width: `${90 - ((l * 13) % 40)}%` }} />)}
          <motion.div initial={{ scale: 2.4, opacity: 0, rotate: -20 }} animate={{ scale: 1, opacity: 1, rotate: -12 }} transition={{ duration: 0.45, delay: 1.2 + i * 0.35, ease: "backOut" }}
            className="absolute bottom-5 right-4 border-[3px] border-emerald-600 text-emerald-700 font-black text-sm px-2 py-1 rounded">SIGNED ✓</motion.div>
        </motion.div>
      ))}
    </div>
  );
}
